<template>
  <b-card tag="article" class="mb-4">
    <h4 class="card-title title-slash">
      {{ $t('dashboard.transactionsWidget.title') }}
    </h4>
    <template v-if="!loading && cleanedInvoices.length > 0">
      <b-table
        responsive
        :items="cleanedInvoices"
        :fields="invoiceTableFields"
        class="table-clean"
      >
        <template v-slot:cell(reference)="data">
          <div
            class="truncate_text"
            style="max-width: 150px"
            :title="data.item.reference"
          >
            <router-link
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : { name: 'invoicesSingle', params: { id: data.item.id } }
              "
              v-track-click
              :id="`transaction-widget-invoice`"
            >
              {{ data.item.reference }}
            </router-link>
          </div>
        </template>
        <template v-slot:cell(date)="data">
          <div :style="'width: 110px'">
            {{ data.value | moment('MMM D, YYYY') }}
          </div>
        </template>
        <template v-slot:cell(value)="data">
          {{ data.value | currency(data.item.currsym, null, 0) }}
        </template>
        <template v-slot:cell(stage_human)="data">
          <div
            class="invoice-status"
            :class="data.item.stage_human | invoiceStageClass"
          >
            {{ data.item.stage_human | invoiceStageDisplay }}
          </div>
        </template>
        <template v-slot:cell(type)="data">
          {{ $t(`dashboard.transactionsWidget.status['${data.item.type}']`) }}
        </template>
        <template v-slot:cell(action)="data">
          <download-dropdown
            :options="fetchDownloadLinks(data)"
            @fireEvent="dropdownEvent"
          />
        </template>
      </b-table>
      <div class="card-actions">
        <router-link
          :to="{ name: 'transactions' }"
          slot="footer"
          v-track-click
          id="transaction-widget-all"
        >
          {{ $t('dashboard.transactionsWidget.viewAll') }}
        </router-link>
      </div>
    </template>
    <template v-else-if="!loading && cleanedInvoices.length === 0">
      <p class="lead">
        {{ $t('dashboard.transactionsWidget.noRecentTransactions') }}
      </p>
      <div class="card-actions">
        <router-link
          :to="{ name: 'invoices' }"
          slot="footer"
          v-track-click
          id="transaction-widget-all"
        >
          {{ $t('dashboard.pendingInvoicesWidget.viewAll') }}
        </router-link>
      </div>
    </template>
    <div v-else>
      <loader :height="268" />
    </div>
  </b-card>
</template>
<script>
import Loader from '@/components/common/Loader'
import DownloadDropdown from '@/components/common/DownloadDropdown'
import invoiceService from '@/services/invoice'
import downloadService from '@/services/download'
import exportersService from '@/services/exporters'

export default {
  props: ['monitoringData'],
  data() {
    return {
      loading: true,
      transactions: []
    }
  },
  mounted() {
    this.fetchPendingInvoices()
  },
  computed: {
    cleanedInvoices() {
      return (
        this.transactions &&
        this.transactions.map((transaction) => {
          return {
            reference: transaction.reference || transaction.invoice_reference,
            importer: transaction.importer || transaction.invoice_importer,
            type: transaction.type,
            date:
              transaction.dated ||
              transaction.settlement_date ||
              transaction.disbursed,
            value: transaction.settlement || transaction.value,
            currsym: transaction.currsym,
            id: transaction.id || transaction.invoice_id,
            swiftAvail: transaction.swiftAvail,
            nocAvail: transaction.nocAvail
          }
        })
      )
    },
    invoiceTableFields() {
      return this.$t('dashboard.transactionsWidget.invoiceTableFields')
    },
    newInvoiceRedirect() {
      if (
        !!this.monitoringData &&
        !!this.monitoringData.mail_date &&
        this.$moment(this.monitoringData.revoke_date).isValid() &&
        this.$moment(this.monitoringData.mail_date).isValid() &&
        (!this.monitoringData.financials.active ||
          !this.monitoringData.gst.active)
      ) {
        const mailDate = new Date(this.monitoringData.mail_date) // Convert to Date object
        const currentDate = new Date() // Get today's date

        // Calculate the date 45 days after the mail_date
        const days60AfterMailDate = new Date(mailDate)
        days60AfterMailDate.setDate(mailDate.getDate() + 60)

        return currentDate >= new Date(this.monitoringData.revoke_date)
      }
      return false
    }
  },
  methods: {
    fetchDownloadLinks(data) {
      return [
        {
          text: this.$t('common.invoiceSwift'),
          data: {
            id: data.item.id,
            type: 'swift',
            invType: data.item.type,
            ref: data.item.reference,
            date:
              data.item.date || data.item.settlement_date || data.item.disbursed
          },
          disabled: !data.item.swiftAvail
        },
        {
          text: this.$t('common.invoiceAdvice'),
          data: {
            id: data.item.id,
            type: 'advice'
          },
          disabled: false
        },
        {
          text: this.$t('common.invoiceNoc'),
          data: {
            id: data.item.advance_id,
            type: 'noc',
            ref: data.item.reference,
            date: data.item.disbursed || data.item.date
          },
          disabled: !data.item.nocAvail
        }
      ]
    },
    downloadInvoiceAdvice(invId) {
      this.$store.dispatch('setDownloadNotification', {
        fileDownloadNotification: true
      })
      return invoiceService
        .downloadInvoice(invId)
        .then((res) => {
          downloadService.downloadPDF(res.data, `invoices-${invId}-advice.pdf`)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
        .catch((e) => {
          console.log(e)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
    },
    async downloadInvoiceSwiftCopy(data) {
      if (data.id) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        if (data.invType === 'advance') {
          let res = await invoiceService
            .downloadSwiftCopy('advances', data.id)
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'advance',
            data.ref,
            data.date
          )
          downloadService.downloadFile(res.data, filename)
        } else {
          let res = await invoiceService
            .downloadSwiftCopy('invoices', data.id, 'settlement')
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'settlement',
            data.ref,
            data.date
          )
          downloadService.downloadFile(res.data, filename)
        }
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    async downloadNocDocument(data) {
      this.$store.dispatch('setDownloadNotification', {
        fileDownloadNotification: true
      })
      if (data.id) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        if (data.invType === 'advance') {
          let res = await invoiceService
            .downloadNocCopy('advances', data.id)
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'advance',
            data.ref,
            data.date
          )
          downloadService.downloadFile(res.data, filename)
        } else {
          let res = await invoiceService
            .downloadNocCopy('invoices', data.id, 'settlement')
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'settlement',
            data.ref,
            data.date
          )
          downloadService.downloadFile(res.data, filename)
        }
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    dropdownEvent(links) {
      if (links.data && links.data.type === 'advice') {
        this.$mpTrack('Advance invoices - Download Invoice advice')
        this.downloadInvoiceAdvice(links.data.id)
      } else if (links.data && links.data.type === 'noc') {
        this.$mpTrack('Advance invoices - Download Noc Document')
        this.downloadNocDocument(links.data)
      } else {
        this.$mpTrack('Advance invoices - Download Swift copy')
        this.downloadInvoiceSwiftCopy(links.data)
      }
    },
    fetchPendingInvoices() {
      this.loading = true
      exportersService
        .fetchExportersTransactions(this.$store.state.account.accountId, true)
        .then(async (res) => {
          this.loading = false
          const transactionData = res.data.slice(0, 5)
          const resData = await invoiceService.checkSwiftCopy(
            'advances',
            transactionData.filter((x) => x.type === 'advance').map((x) => x.id)
          )
          const resData2 = await invoiceService.checkSwiftCopy(
            'invoices',
            transactionData
              .filter((x) => x.type === 'settlement')
              .map((x) => x.invoice_id || x.id)
          )
          if (resData.data) {
            Object.keys(resData.data).forEach((v) => {
              const index = transactionData.findIndex(
                (x) => x.id === parseInt(v)
              )
              transactionData[index].swiftAvail = resData.data[v]
            })
          }
          if (resData2.data) {
            Object.keys(resData2.data).forEach((v) => {
              const index = transactionData.findIndex(
                (x) => x.id === parseInt(v)
              )
              transactionData[index].swiftAvail = resData2.data[v] || false
            })
          }

          const resData3 = await invoiceService.checkNocCopy(
            'advances',
            transactionData.filter((x) => x.type === 'advance').map((x) => x.id)
          )
          const resData4 = await invoiceService.checkNocCopy(
            'invoices',
            transactionData
              .filter((x) => x.type === 'settlement')
              .map((x) => x.invoice_id || x.id)
          )
          if (resData3.data) {
            Object.keys(resData3.data).forEach((v) => {
              const index = transactionData.findIndex(
                (x) => x.id === parseInt(v)
              )
              transactionData[index].nocAvail = resData3.data[v]
            })
          }
          if (resData4.data) {
            Object.keys(resData4.data).forEach((v) => {
              const index = transactionData.findIndex(
                (x) => x.id === parseInt(v)
              )
              transactionData[index].nocAvail = resData4.data[v]
            })
          }
          this.transactions = transactionData
        })
        .catch((e) => {
          console.log(e)
        })
    }
  },
  components: {
    Loader,
    DownloadDropdown
  }
}
</script>
